import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import PieceOfFurniture from "../PieceOfFurniture/PieceOfFurniture";
import LoadingAnimation from "../../components/_common/Loading";
import moduleValuationController from "../../Api/moduleValuationController";
import { useToast } from "../../hooks/useToast";
import { IModuleValuation } from "../../interfaces/IModuleValuation";
import { IModule } from "../../interfaces/IFurniture";
import { Box, Typography, Divider, Paper, Chip, Stack, Card, CardContent } from "@mui/material";
import styled from "@emotion/styled";
import { baseURL } from "../../config";

const BASE_URL = baseURL;

interface IParams {
    valuationId?: string;
}

const ModuleImage = styled('img')`
    height: 90px;
    max-width: 100%;
    object-fit: contain;
    margin: 0;
    padding: 0;
    display: block;
`;

type OrientedModule = IModule & { uuid: string; x: number; y: number; direction: "row" | "column" | "row-reverse" };

function getOrientedModules(modules: (IModule & { uuid: string })[]): OrientedModule[] {
    let x = 0, y = 0;
    let direction: "row" | "column" | "row-reverse" = "row";
    const result: OrientedModule[] = [];

    for (const mod of modules) {
        result.push({ ...mod, x, y, direction });

        if (mod.moduleType === "corner") {
            if (direction === "row") {
                direction = "column";
            } else if (direction === "column") {
                direction = "row-reverse";
            } else if (direction === "row-reverse") {
                direction = "column";
            }
        }

        if (direction === "row") x++;
        else if (direction === "column") y++;
        else if (direction === "row-reverse") x--;
    }

    return result;
}

// --- WYMIARY PROJEKTU ---
type ProjectDimensions = {
    topWidth: number;
    sideDepth: number;
    bottomWidth: number;
    side2Depth: number;
};

function calculateProjectDimensions(orientedModules: OrientedModule[]): ProjectDimensions | null {
    if (orientedModules.length === 0) return null;

    let topWidth = 0;
    let sideDepth = 0;
    let bottomWidth = 0;

    let i = 0;
    // GÓRNY WYMIAR
    for (; i < orientedModules.length; i++) {
        topWidth += orientedModules[i].width;
        if ((orientedModules[i].moduleType === 'corner') || i === orientedModules.length - 1) {
            break;
        }
    }

    // Jeżeli nie ma dalszych modułów, zwróć tylko górny wymiar
    if (i === orientedModules.length - 1) {
        return { topWidth, sideDepth: 0, bottomWidth: 0, side2Depth: 0 };
    }

    // BOCZNY WYMIAR
    i++; // przejdź na kolejny moduł po narożniku
    if (i < orientedModules.length) {
        sideDepth += orientedModules[i - 1].depth; // głębokość narożnika
        for (; i < orientedModules.length; i++) {
            sideDepth += orientedModules[i].width;
            if ((orientedModules[i].moduleType === 'corner') || i === orientedModules.length - 1) {
                break;
            }
        }
    }

    // Jeżeli nie ma dalszych modułów, zwróć górny i boczny wymiar
    if (i === orientedModules.length - 1) {
        return { topWidth, sideDepth, bottomWidth: 0, side2Depth: 0 };
    }

    // DOLNY WYMIAR
    i++; // przejdź na kolejny moduł po drugim narożniku
    if (i < orientedModules.length) {
        bottomWidth += orientedModules[i - 1].depth; // głębokość drugiego narożnika
        for (; i < orientedModules.length; i++) {
            bottomWidth += orientedModules[i].width;
        }
    }

    return { topWidth, sideDepth, bottomWidth, side2Depth: 0 };
}

// Komponent linii z wymiarem
const DimensionLine = ({
    value,
    length,
    orientation = "horizontal",
    labelRight = false,
}: {
    value: number;
    length: number;
    orientation?: "horizontal" | "vertical";
    labelRight?: boolean;
}) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: orientation === "horizontal" ? "column" : "row",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            width: orientation === "horizontal" ? `${length}px` : "auto",
            height: orientation === "vertical" ? `${length}px` : "auto",
        }}
    >
        {orientation === "vertical" && labelRight ? (
            <>
                <Box
                    sx={{
                        width: 2,
                        height: "100%",
                        background: "#1976d2",
                        borderRadius: 1,
                        margin: "0 auto",
                        zIndex: 1,
                    }}
                />
                <Typography
                    sx={{
                        position: "absolute",
                        left: "calc(100% + 8px)",
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontWeight: 600,
                        fontSize: 15,
                        color: "#1976d2",
                        background: "#fff",
                        px: 1,
                        zIndex: 2,
                        pointerEvents: "none",
                        whiteSpace: "nowrap"
                    }}
                >
                    {value} cm
                </Typography>
            </>
        ) : (
            <>
                <Typography
                    sx={{
                        position: "absolute",
                        left: orientation === "horizontal" ? "50%" : "50%",
                        top: orientation === "vertical" ? -18 : "-12px",
                        transform: "translate(-50%, -50%)",
                        fontWeight: 600,
                        fontSize: 15,
                        color: "#1976d2",
                        background: "#fff",
                        px: 1,
                        zIndex: 2,
                        pointerEvents: "none",
                        whiteSpace: "nowrap",
                    }}
                >
                    {value} cm
                </Typography>
                <Box
                    sx={{
                        width: orientation === "horizontal" ? "100%" : 2,
                        height: orientation === "vertical" ? "100%" : 2,
                        background: "#1976d2",
                        borderRadius: 1,
                        margin: "0 auto",
                        zIndex: 1,
                    }}
                />
            </>
        )}
    </Box>
);

const ValuationPage: FC = () => {
    const params: IParams = useParams();
    const [valuation, setValuation] = useState<IModuleValuation | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const toast = useRef(useToast()).current;

    const getValuation = useCallback(async () => {
        setLoading(true);
        if (!params.valuationId) {
            setLoading(false);
            return;
        }
        const res = await moduleValuationController.getModuleValuation(params.valuationId);
        if (!res.ok) {
            toast.error(res.message);
            setLoading(false);
            return;
        }
        setValuation(res.moduleValuation);
        setLoading(false);
    }, [params.valuationId, toast]);

    useEffect(() => {
        getValuation();
    }, [getValuation]);

    // Dodaj uuid do każdego modułu (potrzebne do orientacji)
    const getModulesWithUuid = (modules?: IModule[]) =>
        (modules || []).map((m, idx) => ({ ...m, uuid: m._id || String(idx) }));

    const renderModulesAndMessage = () => {
        if (!valuation?.modules || valuation.modules.length === 0) return null;
        const orientedModules = getOrientedModules(getModulesWithUuid(valuation.modules));
        if (!orientedModules.length) return null;

        // --- WYMIARY ---
        const xs = orientedModules.map(m => m.x);
        const ys = orientedModules.map(m => m.y);
        const minX = Math.min(...xs, 0);
        const maxX = Math.max(...xs, 0);
        const minY = Math.min(...ys, 0);
        const maxY = Math.max(...ys, 0);

        const columns = maxX - minX + 1;
        const rows = maxY - minY + 1;

        // Moduły na górze i dole
        const topModules = orientedModules.filter(m => m.y === minY);
        const bottomModules = orientedModules.filter(m => m.y === maxY);

        // Długości linii (90px szerokości modułu + 3px gap)
        const getLineLength = (mods: OrientedModule[]) =>
            mods.length > 0 ? mods.length * 90 + (mods.length - 1) * 3 : 0;

        const lineLengthTop = getLineLength(topModules);
        const lineLengthBottom = getLineLength(bottomModules);
        const lineLengthY = rows > 0 ? rows * 90 + (rows - 1) * 3 : 0;

        // Pozycja lewej krawędzi linii względem całego gridu
        const getLeftOffset = (mods: OrientedModule[]) => {
            if (mods.length === 0) return 0;
            const firstCol = Math.min(...mods.map(m => m.x));
            return (firstCol - minX) * (90 + 3);
        };

        const leftOffsetTop = getLeftOffset(topModules) + 20;
        const leftOffsetBottom = getLeftOffset(bottomModules) + 20;

        // Oblicz wymiary projektu
        const projectDimensions = calculateProjectDimensions(orientedModules);

        // Białe tło obejmuje całość (moduły + wymiary)
        return (
            <Paper
                elevation={4}
                sx={{
                    maxWidth: '95vw',
                    mx: "auto",
                    mb: 4,
                    mt: 2,
                    borderRadius: 4,
                    background: "#fff",
                    boxShadow: "0 4px 24px 0 #e0e7ef",
                    p: { xs: 2, sm: 3, md: 4 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                }}
            >
                <Box sx={{ width: "100%" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 20, textAlign: 'center', mb: 2, color: "#1976d2", letterSpacing: 1 }}>
                        Moduły w projekcie
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                        justifyContent="center"
                        sx={{ mb: 2 }}
                    >
                        {orientedModules.map(module => (
                            <Chip
                                key={module.uuid}
                                label={module.name}
                                color="primary"
                                variant="filled"
                                sx={{
                                    fontWeight: 500,
                                    fontSize: 15,
                                    mb: 1,
                                    px: 2,
                                    py: 1,
                                    borderRadius: 2,
                                    background: "#e3eefd",
                                    borderColor: "#1976d2",
                                    color: "#1976d2",
                                    letterSpacing: 0.5,
                                }}
                            />
                        ))}
                    </Stack>
                </Box>
                {/* --- WYMIARY + GRID --- */}
                <Box
                    sx={{
                        position: "relative",
                        width: columns * 90 + (columns - 1) * 3 + 40, // +40px na białe tło po bokach
                        margin: "0 auto",
                        mt: 2,
                        mb: 2,
                        p: 0,
                        background: "transparent",
                        borderRadius: 3,
                        minHeight: 120,
                        minWidth: 120,
                        display: "inline-block",
                    }}
                >
                    {/* Górna linia */}
                    {projectDimensions && projectDimensions.topWidth > 0 && (
                        <Box
                            sx={{
                                position: "absolute",
                                left: leftOffsetTop,
                                top: 0,
                                zIndex: 10,
                                width: lineLengthTop
                            }}
                        >
                            <DimensionLine
                                value={projectDimensions.topWidth}
                                length={lineLengthTop}
                                orientation="horizontal"
                            />
                        </Box>
                    )}
                    {/* Boczna linia po PRAWEJ */}
                    {projectDimensions && projectDimensions.sideDepth > 0 && (
                        <Box sx={{ position: "absolute", right: -5, top: 20, bottom: 20, zIndex: 10 }}>
                            <DimensionLine
                                value={projectDimensions.sideDepth}
                                length={lineLengthY - 40}
                                orientation="vertical"
                                labelRight
                            />
                        </Box>
                    )}
                    {/* Dolna linia */}
                    {projectDimensions && projectDimensions.bottomWidth > 0 && (
                        <Box
                            sx={{
                                position: "absolute",
                                left: leftOffsetBottom,
                                bottom: -25,
                                zIndex: 10,
                                width: lineLengthBottom
                            }}
                        >
                            <DimensionLine
                                value={projectDimensions.bottomWidth}
                                length={lineLengthBottom}
                                orientation="horizontal"
                            />
                        </Box>
                    )}
                    {/* Grid z modułami */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${columns}, 1fr)`,
                            gridAutoRows: 'minmax(90px, auto)',
                            gap: 0,
                            justifyItems: 'center',
                            alignItems: 'center',
                            minHeight: '100px',
                            width: 'auto',
                            margin: '20px',
                            position: "relative",
                            zIndex: 2,
                            p: 0
                        }}
                    >
                        {orientedModules.map((module) => {
                            let rotate = 0;
                            if (module.direction === "column") rotate = 90;
                            if (module.direction === "row-reverse") rotate = 180;

                            return (
                                <Box
                                    key={module.uuid}
                                    sx={{
                                        gridColumn: module.x - minX + 1,
                                        gridRow: module.y - minY + 1,
                                        background: "transparent",
                                        boxShadow: "none",
                                        border: "none",
                                        p: 0,
                                        m: 0,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: 90,
                                        height: 90
                                    }}
                                >
                                    <ModuleImage
                                        src={BASE_URL + module.image}
                                        alt={module.name}
                                        style={{
                                            transform: `rotate(${rotate}deg)`,
                                            transition: "transform 0.2s",
                                            margin: 0,
                                            padding: 0,
                                            display: "block"
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
                {valuation.message && (
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: 600,
                            mt: 2,
                            background: "#f8f9fa",
                            borderLeft: "6px solid #1976d2",
                            borderRadius: 2,
                            p: 2.5,
                            boxShadow: 1,
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 1, color: "#1976d2", fontWeight: 700 }}>
                            Wiadomość od klienta
                        </Typography>
                        <Typography sx={{ whiteSpace: "pre-line", fontSize: 17 }}>
                            {valuation.message}
                        </Typography>
                    </Box>
                )}
            </Paper>
        );
    };

    const renderStandAloneModules = () => {
        if (!valuation?.standAloneModules || valuation.standAloneModules.length === 0) return null;
        return (
            <Card
                elevation={2}
                sx={{
                    maxWidth: 600,
                    mx: "auto",
                    mb: 4,
                    borderRadius: 4,
                    background: "#f8fafc",
                    boxShadow: "0 2px 8px 0 #e0e7ef",
                    p: { xs: 1, sm: 2 }
                }}
            >
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, textAlign: "center", color: "#1976d2" }}>
                        Moduły wolnostojące
                    </Typography>
                    <Box sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        justifyContent: "center",
                    }}>
                        {valuation.standAloneModules.map((module, idx) => (
                            <Box
                                key={module._id || idx}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    maxWidth: 165,
                                    margin: 2,
                                    textAlign: "center",
                                    background: "#fff",
                                    borderRadius: 2,
                                    boxShadow: 1,
                                    padding: 2,
                                    minWidth: 140,
                                    flex: "1 1 140px",
                                    position: "relative",
                                }}
                            >
                                <ModuleImage src={BASE_URL + module.image} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>
                                    {module.name}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </CardContent>
            </Card>
        );
    };

    if (loading) return (
        <PieceOfFurniture>
            <LoadingAnimation loadingText="Wczytywanie wyceny" />
        </PieceOfFurniture>
    );


    return (
        <PieceOfFurniture>
            <Divider sx={{ mb: 3 }} />
            {renderModulesAndMessage()}
            {renderStandAloneModules()}
        </PieceOfFurniture>
    );
};

export default ValuationPage;